import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "../utils";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";
import { applyMoveToColumns, mergeMovedTask, pickMoveFields } from "../../Components/tasks/taskBoardUtils";
import {
  applyChecklistResult,
  applyChecklistToggle,
  applyChecklistReorder,
} from "../../Components/tasks/taskChecklistUtils";

// İş Planlama — server: /api/tasks/storageRoles (hepsi POST).
// Contract: BayinerIOT-Workspace-/docs/api-contracts/tasks.md
const BASE = "/api/tasks/storageRoles";
const call = (path) => (data) => apiFunction(data, `${BASE}/${path}`, "POST");

const taskTag = (result, arg) => {
  const tags = [];
  const id = result?.data?.task?._id || arg?.id;
  if (id) tags.push({ type: "Task", id });
  if (arg?.key) tags.push({ type: "Task", id: arg.key });
  return tags;
};
const TASK_WRITE = ["TaskList", "TaskBoard"];

// Aynı işi gösteren tüm getTask cache girdilerine güncel TaskDetail'i yazar.
function patchTaskCaches(dispatch, getState, fresh) {
  const cache = getState()[tasksApi.reducerPath]?.queries || {};
  Object.values(cache).forEach((q) => {
    if (q?.endpointName !== "getTask" || q.data?.data?.task?._id !== fresh._id) return;
    dispatch(
      tasksApi.util.updateQueryData("getTask", q.originalArgs, (draft) => {
        if (draft?.data) draft.data.task = fresh;
      }),
    );
  });
}

// Alt işin değişimi üst işin detayını (subtasks, subtaskCount) etkiler; arg.parent verilirse o da tazelenir.
const parentTag = (arg) => (arg?.parent ? [{ type: "Task", id: arg.parent }] : []);

// Verilen işin (_id) tüm getTask cache girdilerine recipe uygular; patch listesi döner (undo için).
function patchTaskById(dispatch, getState, taskId, recipe) {
  const patches = [];
  const cache = getState()[tasksApi.reducerPath]?.queries || {};
  Object.values(cache).forEach((q) => {
    if (q?.endpointName !== "getTask" || q.data?.data?.task?._id !== taskId) return;
    patches.push(
      dispatch(
        tasksApi.util.updateQueryData("getTask", q.originalArgs, (draft) => {
          if (draft?.data?.task) recipe(draft.data.task);
        }),
      ),
    );
  });
  return patches;
}

// Kontrol listesi uçları: yanıt { checklist, checklistCount } açık getTask cache'lerine yazılır
// (version artmaz — contract). optimistic(task, arg) verilirse istekten önce uygulanır, hatada geri alınır.
// Liste/pano sayaçları tazelenir; geçmiş yazan uçlarda (withActivity) detay da tazelenir.
const checklistMutation = (builder, path, { optimistic, withActivity = false } = {}) =>
  builder.mutation({
    query: call(`checklist/${path}`),
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      const undo = optimistic
        ? patchTaskById(dispatch, getState, arg.task, (t) => optimistic(t, arg))
        : [];
      try {
        const { data } = await queryFulfilled;
        const result = data?.data;
        if (result?.checklist) {
          patchTaskById(dispatch, getState, arg.task, (t) => applyChecklistResult(t, result));
        }
      } catch {
        undo.forEach((p) => p.undo());
      }
    },
    invalidatesTags: (r, e, arg) =>
      e ? [] : [...TASK_WRITE, ...(withActivity ? [{ type: "Task", id: arg.task }] : [])],
  });

const tasksApi = createApi({
  reducerPath: "tasks",
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  tagTypes: ["TaskProjects", "TaskLabels", "TaskList", "TaskBoard", "Task", "TaskComments"],
  keepUnusedDataFor: 60,
  endpoints(builder) {
    return {
      // Projeler
      getTaskProjects: builder.query({
        query: call("projects/list"),
        providesTags: ["TaskProjects"],
      }),
      createTaskProject: builder.mutation({
        query: call("projects/create"),
        invalidatesTags: ["TaskProjects"],
      }),
      updateTaskProject: builder.mutation({
        query: call("projects/update"),
        invalidatesTags: ["TaskProjects"],
      }),
      archiveTaskProject: builder.mutation({
        query: call("projects/archive"),
        invalidatesTags: ["TaskProjects"],
      }),
      setTaskProjectMembers: builder.mutation({
        query: call("projects/members/set"),
        invalidatesTags: ["TaskProjects"],
      }),

      // Etiketler
      getTaskLabels: builder.query({
        query: call("labels/list"),
        providesTags: ["TaskLabels"],
      }),
      createTaskLabel: builder.mutation({
        query: call("labels/create"),
        invalidatesTags: ["TaskLabels"],
      }),
      updateTaskLabel: builder.mutation({
        query: call("labels/update"),
        invalidatesTags: ["TaskLabels"],
      }),
      deleteTaskLabel: builder.mutation({
        query: call("labels/delete"),
        invalidatesTags: ["TaskLabels", "TaskList", "TaskBoard"],
      }),

      // Kişiler
      getTaskAssignees: builder.query({ query: call("assignees") }),

      // İşler
      getTasks: builder.query({
        query: call("list"),
        providesTags: ["TaskList"],
      }),
      getTaskBoard: builder.query({
        query: call("board"),
        providesTags: ["TaskBoard"],
      }),
      getTask: builder.query({
        query: call("get"),
        providesTags: (result, error, arg) => taskTag(result, arg),
      }),
      // 1.1: arg.parent ile alt iş; üst işin detayı (subtasks) tazelenir.
      createTask: builder.mutation({
        query: call("create"),
        invalidatesTags: (r, e, arg) => [...TASK_WRITE, ...parentTag(arg)],
      }),
      // Yanıttaki güncel iş (başarıda data.task, 409'da error.data.data.task) açık
      // getTask cache'lerine hemen yazılır; art arda kayıtlar güncel version'la gider.
      updateTask: builder.mutation({
        query: call("update"),
        async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
          let fresh;
          try {
            const { data } = await queryFulfilled;
            fresh = data?.data?.task;
          } catch (e) {
            fresh = e?.error?.data?.data?.task;
          }
          if (fresh) patchTaskCaches(dispatch, getState, fresh);
        },
        invalidatesTags: (r, e, arg) => [...TASK_WRITE, ...taskTag(r, arg), ...parentTag(arg)],
      }),
      // Pano iyimser güncelleme: eşleşen getTaskBoard cache girdilerinde kart taşınır,
      // hata olursa geri alınır. Başarıda yanıttaki version/boardOrder/status pano ve
      // getTask cache'lerine hemen yazılır (tazeleme gelmeden tekrar sürüklemede 409 olmasın);
      // ardından TaskBoard/TaskList tazelenir.
      moveTask: builder.mutation({
        query: call("move"),
        async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
          const patches = [];
          const cache = getState()[tasksApi.reducerPath]?.queries || {};
          Object.values(cache).forEach((q) => {
            if (q?.endpointName !== "getTaskBoard" || !q.data) return;
            patches.push(
              dispatch(
                tasksApi.util.updateQueryData("getTaskBoard", q.originalArgs, (draft) => {
                  const cols = draft?.data?.columns;
                  if (!cols) return;
                  draft.data.columns = applyMoveToColumns(cols, {
                    taskId: arg.id,
                    status: arg.status,
                    beforeId: arg.beforeId,
                    afterId: arg.afterId,
                  });
                }),
              ),
            );
          });
          let moved;
          try {
            const { data } = await queryFulfilled;
            moved = data?.data?.task;
          } catch {
            patches.forEach((p) => p.undo());
            return;
          }
          if (!moved?._id) return;
          const after = getState()[tasksApi.reducerPath]?.queries || {};
          Object.values(after).forEach((q) => {
            if (!q?.data) return;
            if (q.endpointName === "getTaskBoard") {
              dispatch(
                tasksApi.util.updateQueryData("getTaskBoard", q.originalArgs, (draft) => {
                  const cols = draft?.data?.columns;
                  if (cols) draft.data.columns = mergeMovedTask(cols, moved);
                }),
              );
            } else if (q.endpointName === "getTask" && q.data?.data?.task?._id === moved._id) {
              dispatch(
                tasksApi.util.updateQueryData("getTask", q.originalArgs, (draft) => {
                  if (draft?.data?.task) Object.assign(draft.data.task, pickMoveFields(moved));
                }),
              );
            }
          });
        },
        invalidatesTags: (r, e, arg) => [...TASK_WRITE, { type: "Task", id: arg.id }],
      }),
      watchTask: builder.mutation({
        query: call("watch"),
        invalidatesTags: (r, e, arg) => [{ type: "Task", id: arg.id }],
      }),
      unwatchTask: builder.mutation({
        query: call("unwatch"),
        invalidatesTags: (r, e, arg) => [{ type: "Task", id: arg.id }],
      }),
      deleteTask: builder.mutation({
        query: call("delete"),
        invalidatesTags: (r, e, arg) => [...TASK_WRITE, { type: "Task", id: arg.id }, ...parentTag(arg)],
      }),
      // 1.7 — arşiv: üst iş alt işleriyle birlikte gider/gelir, açık detaylar da yenilensin.
      archiveTask: builder.mutation({
        query: call("archive"),
        invalidatesTags: (r, e) => (e ? [] : [...TASK_WRITE, "Task"]),
      }),
      unarchiveTask: builder.mutation({
        query: call("unarchive"),
        invalidatesTags: (r, e) => (e ? [] : [...TASK_WRITE, "Task"]),
      }),

      // Kontrol listesi (1.1) — hepsi { token, task, … }
      addTaskChecklistItem: checklistMutation(builder, "add", { withActivity: true }),
      updateTaskChecklistItem: checklistMutation(builder, "update"),
      toggleTaskChecklistItem: checklistMutation(builder, "toggle", {
        withActivity: true,
        optimistic: (t, arg) => applyChecklistToggle(t, arg.itemId, arg.done),
      }),
      removeTaskChecklistItem: checklistMutation(builder, "remove", { withActivity: true }),
      reorderTaskChecklist: checklistMutation(builder, "reorder", {
        optimistic: (t, arg) => applyChecklistReorder(t, arg.itemIds),
      }),

      // Yorumlar
      getTaskComments: builder.query({
        query: call("comments/list"),
        providesTags: (r, e, arg) => [{ type: "TaskComments", id: arg.task }],
      }),
      createTaskComment: builder.mutation({
        query: call("comments/create"),
        invalidatesTags: (r, e, arg) => [
          { type: "TaskComments", id: arg.task },
          { type: "Task", id: arg.task },
          ...TASK_WRITE,
        ],
      }),
      updateTaskComment: builder.mutation({
        query: call("comments/update"),
        invalidatesTags: (r, e, arg) => {
          const task = r?.data?.comment?.task || arg.task;
          return task
            ? [{ type: "TaskComments", id: task }, { type: "Task", id: task }]
            : ["TaskComments", "Task"];
        },
      }),
      deleteTaskComment: builder.mutation({
        query: call("comments/delete"),
        // yanıt boş; task bilinmiyorsa (arg.task verilmediyse) tüm yorum listeleri tazelenir
        invalidatesTags: (r, e, arg) =>
          arg.task
            ? [{ type: "TaskComments", id: arg.task }, { type: "Task", id: arg.task }, ...TASK_WRITE]
            : ["TaskComments", "Task", ...TASK_WRITE],
      }),

      // Ekler
      requestTaskUpload: builder.mutation({ query: call("attachments/requestUpload") }),
      confirmTaskUpload: builder.mutation({
        query: call("attachments/confirm"),
        invalidatesTags: (r, e, arg) => [
          ...(arg.task ? [{ type: "Task", id: arg.task }] : ["Task"]),
          ...TASK_WRITE,
        ],
      }),
      getTaskDownloadUrl: builder.query({
        query: call("attachments/downloadUrl"),
        keepUnusedDataFor: 0,
      }),
      deleteTaskAttachment: builder.mutation({
        query: call("attachments/delete"),
        invalidatesTags: (r, e, arg) => [
          ...(arg.task ? [{ type: "Task", id: arg.task }] : ["Task"]),
          ...TASK_WRITE,
        ],
      }),
    };
  },
});

export const {
  useGetTaskProjectsQuery,
  useCreateTaskProjectMutation,
  useUpdateTaskProjectMutation,
  useArchiveTaskProjectMutation,
  useSetTaskProjectMembersMutation,
  useGetTaskLabelsQuery,
  useCreateTaskLabelMutation,
  useUpdateTaskLabelMutation,
  useDeleteTaskLabelMutation,
  useGetTaskAssigneesQuery,
  useGetTasksQuery,
  useGetTaskBoardQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useMoveTaskMutation,
  useWatchTaskMutation,
  useUnwatchTaskMutation,
  useDeleteTaskMutation,
  useArchiveTaskMutation,
  useUnarchiveTaskMutation,
  useAddTaskChecklistItemMutation,
  useUpdateTaskChecklistItemMutation,
  useToggleTaskChecklistItemMutation,
  useRemoveTaskChecklistItemMutation,
  useReorderTaskChecklistMutation,
  useGetTaskCommentsQuery,
  useCreateTaskCommentMutation,
  useUpdateTaskCommentMutation,
  useDeleteTaskCommentMutation,
  useRequestTaskUploadMutation,
  useConfirmTaskUploadMutation,
  useLazyGetTaskDownloadUrlQuery,
  useDeleteTaskAttachmentMutation,
} = tasksApi;
export { tasksApi };
